import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import { prepareFilters, preparePagination } from '@/utils/apiParamsUtils';

const API_BASE = '/payroll-automation';

/**
 * Get automation configuration for a company
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Automation configuration
 */
export const getAutomationConfig = async (companyId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/config/${companyId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get automation config:', error);
    throw error;
  }
};

/**
 * Create automation configuration
 * @param {Object} configData - Configuration data
 * @returns {Promise<Object>} Created configuration
 */
export const createAutomationConfig = async (configData) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/config`, configData);
    return response.data;
  } catch (error) {
    console.error('Failed to create automation config:', error);
    throw error;
  }
};

/**
 * Update automation configuration
 * @param {string} configId - Configuration ID
 * @param {Object} configData - Updated configuration data
 * @returns {Promise<Object>} Updated configuration
 */
export const updateAutomationConfig = async (configId, configData) => {
  try {
    const response = await axiosClient.put(
      `${API_BASE}/config/${configId}`,
      configData
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update automation config:', error);
    throw error;
  }
};

/**
 * Delete automation configuration
 * @param {string} configId - Configuration ID
 * @returns {Promise<Object>} Delete response
 */
export const deleteAutomationConfig = async (configId) => {
  try {
    const response = await axiosClient.delete(`${API_BASE}/config/${configId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete automation config:', error);
    throw error;
  }
};

/**
 * Toggle automation enabled/disabled
 * @param {string} configId - Configuration ID
 * @param {boolean} enabled - Enable or disable
 * @returns {Promise<Object>} Updated configuration
 */
export const toggleAutomation = async (configId, enabled) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/config/${configId}/toggle`,
      { enabled }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to toggle automation:', error);
    throw error;
  }
};

/**
 * Execute automation rule manually
 * @param {string} ruleType - Rule type to execute
 * @param {Object} data - Execution data (companyId, etc.)
 * @returns {Promise<Object>} Execution result
 */
export const executeAutomation = async (ruleType, data) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/execute/${ruleType}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Failed to execute automation:', error);
    throw error;
  }
};

/**
 * Get automation execution logs
 * @param {Object} params - Query parameters (filters, pagination)
 * @returns {Promise<Object>} Logs and pagination info
 */
export const getAutomationLogs = async (params) => {
  try {
    const filters = prepareFilters(params, {
      company: 'default',
      ruleType: 'default',
      status: 'default',
      startDate: 'default',
      endDate: 'default',
    });

    const pagination = preparePagination(params);
    const queryParams = qs.stringify({ ...filters, ...pagination });

    const response = await axiosClient.get(`${API_BASE}/logs?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get automation logs:', error);
    throw error;
  }
};

/**
 * Get automation log details
 * @param {string} logId - Log ID
 * @returns {Promise<Object>} Log details
 */
export const getAutomationLogDetails = async (logId) => {
  try {
    const response = await axiosClient.get(`${API_BASE}/logs/${logId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get automation log details:', error);
    throw error;
  }
};

/**
 * Retry failed automation
 * @param {string} logId - Log ID
 * @returns {Promise<Object>} Retry result
 */
export const retryAutomation = async (logId) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/logs/${logId}/retry`);
    return response.data;
  } catch (error) {
    console.error('Failed to retry automation:', error);
    throw error;
  }
};

/**
 * Get workflow executions
 * @param {Object} params - Query parameters (filters, pagination)
 * @returns {Promise<Object>} Workflows and pagination info
 */
export const getWorkflows = async (params) => {
  try {
    const filters = prepareFilters(params, {
      company: 'default',
      workflowName: 'default',
      status: 'default',
      startDate: 'default',
      endDate: 'default',
    });

    const pagination = preparePagination(params);
    const queryParams = qs.stringify({ ...filters, ...pagination });

    const response = await axiosClient.get(
      `${API_BASE}/workflows?${queryParams}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get workflows:', error);
    throw error;
  }
};

/**
 * Get workflow execution details
 * @param {string} executionId - Execution ID
 * @returns {Promise<Object>} Workflow details
 */
export const getWorkflowDetails = async (executionId) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/workflows/${executionId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get workflow details:', error);
    throw error;
  }
};

/**
 * Cancel running workflow
 * @param {string} executionId - Execution ID
 * @returns {Promise<Object>} Cancel result
 */
export const cancelWorkflow = async (executionId) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/workflows/cancel/${executionId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to cancel workflow:', error);
    throw error;
  }
};

/**
 * Delete workflow execution
 * @param {string} executionId - Workflow execution ID
 * @returns {Promise<Object>} Response
 */
export const deleteWorkflow = async (executionId) => {
  try {
    const response = await axiosClient.delete(
      `${API_BASE}/workflows/${executionId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to delete workflow:', error);
    throw error;
  }
};

/**
 * Run monthly payroll workflow
 * @param {Object} data - Workflow data (companyId, month, year)
 * @returns {Promise<Object>} Workflow execution
 */
export const runMonthlyPayrollWorkflow = async (data) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/workflows/monthly-payroll`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Failed to run monthly payroll workflow:', error);
    throw error;
  }
};

/**
 * Trigger AB Soft sync
 * @param {Object} data - Sync data (companyId, month, year, syncType)
 * @returns {Promise<Object>} Sync result
 */
export const syncToAbSoft = async (data) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/absoft/sync`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to sync to AB Soft:', error);
    throw error;
  }
};

/**
 * Get AB Soft sync status
 * @param {string} syncId - Sync ID
 * @returns {Promise<Object>} Sync status
 */
export const getAbSoftSyncStatus = async (syncId) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/absoft/status/${syncId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get AB Soft sync status:', error);
    throw error;
  }
};

/**
 * Get AB Soft sync history
 * @param {Object} params - Query parameters (companyId, filters, pagination)
 * @returns {Promise<Object>} Sync history and pagination info
 */
export const getAbSoftSyncHistory = async (params) => {
  try {
    const filters = prepareFilters(params, {
      companyId: 'required',
      month: 'default',
      year: 'default',
      status: 'default',
      startDate: 'default',
      endDate: 'default',
    });

    const pagination = preparePagination(params);
    const queryParams = qs.stringify({ ...filters, ...pagination });

    const response = await axiosClient.get(
      `${API_BASE}/absoft/history?${queryParams}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get AB Soft sync history:', error);
    throw error;
  }
};

/**
 * Retry failed AB Soft sync
 * @param {string} syncId - Sync ID
 * @returns {Promise<Object>} Retry result
 */
export const retryAbSoftSync = async (syncId) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/absoft/retry/${syncId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to retry AB Soft sync:', error);
    throw error;
  }
};

// ============================================
// Automation Companies API
// ============================================

/**
 * Get all automation companies
 * @param {Object} params - Query parameters (filters, pagination)
 * @returns {Promise<Object>} Companies and pagination info
 */
export const getAutomationCompanies = async (params = {}) => {
  try {
    const queryParams = qs.stringify(params);
    const response = await axiosClient.get(
      `${API_BASE}/companies?${queryParams}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get automation companies:', error);
    throw error;
  }
};

/**
 * Get automation companies for dropdown
 * @returns {Promise<Object>} Companies list
 */
export const getAutomationCompaniesDropdown = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE}/companies/dropdown`);
    return response.data;
  } catch (error) {
    console.error('Failed to get automation companies dropdown:', error);
    throw error;
  }
};

/**
 * Get automation company by ID
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Company details
 */
export const getAutomationCompanyById = async (companyId) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/companies/${companyId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get automation company:', error);
    throw error;
  }
};

/**
 * Create automation company
 * @param {Object} data - Company data
 * @returns {Promise<Object>} Created company
 */
export const createAutomationCompany = async (data) => {
  try {
    const response = await axiosClient.post(`${API_BASE}/companies`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create automation company:', error);
    throw error;
  }
};

/**
 * Update automation company
 * @param {string} companyId - Company ID
 * @param {Object} data - Updated company data
 * @returns {Promise<Object>} Updated company
 */
export const updateAutomationCompany = async (companyId, data) => {
  try {
    const response = await axiosClient.put(
      `${API_BASE}/companies/${companyId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update automation company:', error);
    throw error;
  }
};

/**
 * Delete (deactivate) automation company
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Delete result
 */
export const deleteAutomationCompany = async (companyId) => {
  try {
    const response = await axiosClient.delete(
      `${API_BASE}/companies/${companyId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to delete automation company:', error);
    throw error;
  }
};

/**
 * Link automation company to HLB company
 * @param {string} companyId - Automation company ID
 * @param {string} hlbCompanyId - HLB company ID
 * @returns {Promise<Object>} Updated company
 */
export const linkToHLBCompany = async (companyId, hlbCompanyId) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/companies/${companyId}/link`,
      { hlbCompanyId }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to link to HLB company:', error);
    throw error;
  }
};

/**
 * Unlink automation company from HLB company
 * @param {string} companyId - Automation company ID
 * @returns {Promise<Object>} Updated company
 */
export const unlinkFromHLBCompany = async (companyId) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/companies/${companyId}/unlink`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to unlink from HLB company:', error);
    throw error;
  }
};

// ============================================
// Payroll Submissions API
// ============================================

/**
 * Upload and parse EML file (Dream Point format)
 * @param {FormData} formData - FormData with file and companyId
 * @returns {Promise<Object>} Created submission
 */
export const uploadEmlFile = async (formData) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/submissions/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to upload EML file:', error);
    throw error;
  }
};

/**
 * Upload and parse PCCC files (EML + AB Soft Excel)
 * Uses Claude AI to extract Excel from EML, parse PCCC data, and map employees to AB Soft
 *
 * @param {FormData} formData - FormData with:
 *   - emlFiles: One or more .eml files containing PCCC Excel attachments
 *   - abSoftFiles: One or more AB Soft Excel files (.xlsx, .xls) with employee master list
 *   - companyId: Automation company ID
 *   - month: Month number (1-12)
 *   - year: Year
 *   - projectCode: PCCC project code (e.g., METRO_L1P1D, BBP_BGD)
 * @returns {Promise<Object>} Created submission with parsed and mapped employee data
 */
export const uploadPCCCFiles = async (formData) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/submissions/upload-pccc`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 900000, // 15 minutes - Multi-agent pipeline batching can take time
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to upload PCCC files:', error);
    throw error;
  }
};

/**
 * Upload and parse CCECC files (EML with Excel+PDF attachments + AB Soft Excel)
 * Parses master employee list, attendance matrix, overtime/leave/travel PDFs
 *
 * @param {FormData} formData - FormData with:
 *   - emlFiles: One or more .eml files containing CCECC Excel/PDF attachments
 *   - abSoftFiles: One or more AB Soft Excel files (.xlsx, .xls)
 *   - companyId: Automation company ID
 *   - month: Month number (1-12)
 *   - year: Year
 * @returns {Promise<Object>} Created submission with aggregated employee data
 */
export const uploadCCECCFiles = async (formData) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/submissions/upload-ccecc`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 minutes - PDF parsing takes time
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to upload CCECC files:', error);
    throw error;
  }
};

/**
 * Get payroll submissions
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Submissions and pagination
 */
export const getSubmissions = async (params = {}) => {
  try {
    const queryParams = qs.stringify(params);
    const response = await axiosClient.get(
      `${API_BASE}/submissions?${queryParams}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get submissions:', error);
    throw error;
  }
};

/**
 * Get submission by ID
 * @param {string} submissionId - Submission ID
 * @returns {Promise<Object>} Submission details
 */
export const getSubmissionById = async (submissionId) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/submissions/${submissionId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get submission:', error);
    throw error;
  }
};

/**
 * Update submission
 * @param {string} submissionId - Submission ID
 * @param {Object} data - Update data
 * @returns {Promise<Object>} Updated submission
 */
export const updateSubmission = async (submissionId, data) => {
  try {
    const response = await axiosClient.put(
      `${API_BASE}/submissions/${submissionId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update submission:', error);
    throw error;
  }
};

/**
 * Delete submission
 * @param {string} submissionId - Submission ID
 * @returns {Promise<Object>} Delete result
 */
export const deleteSubmission = async (submissionId) => {
  try {
    const response = await axiosClient.delete(
      `${API_BASE}/submissions/${submissionId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to delete submission:', error);
    throw error;
  }
};

/**
 * Change submission status
 * @param {string} submissionId - Submission ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Updated submission
 */
export const changeSubmissionStatus = async (submissionId, status) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/submissions/${submissionId}/status`,
      { status }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to change submission status:', error);
    throw error;
  }
};

/**
 * Add employee to submission
 * @param {string} submissionId - Submission ID
 * @param {Object} employeeData - Employee data
 * @returns {Promise<Object>} Added employee
 */
export const addEmployeeToSubmission = async (submissionId, employeeData) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/submissions/${submissionId}/employees`,
      employeeData
    );
    return response.data;
  } catch (error) {
    console.error('Failed to add employee:', error);
    throw error;
  }
};

/**
 * Update employee in submission
 * @param {string} submissionId - Submission ID
 * @param {string} employeeId - Employee ID
 * @param {Object} data - Update data
 * @returns {Promise<Object>} Updated employee
 */
export const updateEmployeeInSubmission = async (
  submissionId,
  employeeId,
  data
) => {
  try {
    const response = await axiosClient.put(
      `${API_BASE}/submissions/${submissionId}/employees/${employeeId}`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update employee:', error);
    throw error;
  }
};

/**
 * Remove employee from submission
 * @param {string} submissionId - Submission ID
 * @param {string} employeeId - Employee ID
 * @returns {Promise<Object>} Delete result
 */
export const removeEmployeeFromSubmission = async (
  submissionId,
  employeeId
) => {
  try {
    const response = await axiosClient.delete(
      `${API_BASE}/submissions/${submissionId}/employees/${employeeId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to remove employee:', error);
    throw error;
  }
};

/**
 * Create timesheets (Salary documents) from a payroll submission
 * @param {string} submissionId - Submission ID
 * @returns {Promise<Object>} { createdCount, skippedCount, warnings }
 */
export const createTimesheetsFromSubmission = async (submissionId) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/submissions/${submissionId}/create-timesheets`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to create timesheets:', error);
    throw error;
  }
};

/**
 * Export submission to Excel
 * @param {string} submissionId - Submission ID
 * @returns {Promise<void>} Downloads Excel file
 */
export const exportSubmissionToExcel = async (submissionId) => {
  try {
    const response = await axiosClient.get(
      `${API_BASE}/submissions/${submissionId}/export`,
      {
        responseType: 'blob',
      }
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `payroll_export_${submissionId}.xlsx`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=([^;\n]*)/);
      if (match && match[1]) {
        filename = decodeURIComponent(match[1].replace(/['"]/g, ''));
      }
    }

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export submission:', error);
    throw error;
  }
};

// ============================================
// Agent Configuration - Reference Files (Knowledge Base)
// ============================================

/**
 * Upload reference file to agent configuration
 */
export const uploadReferenceFile = async (configId, formData) => {
  try {
    const response = await axiosClient.post(
      `${API_BASE}/agent-config/${configId}/reference-files`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to upload reference file:', error);
    throw error;
  }
};

/**
 * Delete reference file from agent configuration
 */
export const deleteReferenceFile = async (configId, fileId) => {
  try {
    const response = await axiosClient.delete(
      `${API_BASE}/agent-config/${configId}/reference-files/${fileId}`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to delete reference file:', error);
    throw error;
  }
};
